import React from "react";

import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography
} from "@material-ui/core";
import { GetApp as DownloadIcon } from "@material-ui/icons";

// import { AssignedCreativesList } from "../AssignedCreatives/AssignedCreativesList";

export default function CreativePreviewDialog({
  setOpen,
  previewDialogOpen,
  setPreviewDialogOpen,
  creativePreviewURL,
  selectedCreativePreview,
  previewType = "iframe",
  creativeName,
  creativeId,
  creativeType,
  email,
  timestamp
}) {
  const handleCloseDialog = () => {
    setPreviewDialogOpen(false);
    setOpen(true)
  };

  const getFileExtension = (mimeType, url, type) => {
    // Try MIME type first (ignore generic octet-stream)
    if (mimeType && mimeType !== "application/octet-stream") {
      const sub = mimeType.split("/")[1];
      if (sub) return sub.replace("jpeg", "jpg");
    }
    // Try URL extension
    try {
      const pathname = new URL(url).pathname;
      const match = pathname.match(/\.(\w{2,5})$/);
      if (match) return match[1].toLowerCase();
    } catch {}
    // Fall back to previewType
    const typeMap = { video: "mp4", audio: "mp3", image: "png" };
    return typeMap[type] || "bin";
  };

  const handleDownload = async () => {
    if (!creativePreviewURL) return;
    let blobUrl = null;
    try {
      const response = await fetch(creativePreviewURL);
      const blob = await response.blob();
      const ext = getFileExtension(blob.type, creativePreviewURL, previewType);
      const filename = (creativeName || `creative-${creativeId}`).replace(/[^a-zA-Z0-9_-]/g, "_") + "." + ext;
      blobUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch {
      // Fallback: open in new tab if fetch fails (e.g. CORS)
      window.open(creativePreviewURL, "_blank");
    } finally {
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    }
  };

  // Get the creative's native dimensions, falling back to 720x480
  const getNativeDimensions = () => {
    const size = selectedCreativePreview?.size;
    const w = size?.width && size.width > 0 ? size.width : 720;
    const h = size?.height && size.height > 0 ? size.height : 480;
    return { w, h };
  };

  // Scale dimensions to fit within the viewport while preserving aspect ratio.
  // Only scales DOWN — small creatives stay at native size to avoid blurriness.
  // Enforces a minimum dialog size so tiny creatives (e.g. 320x50) don't look awkward.
  const getDisplayDimensions = () => {
    const { w, h } = getNativeDimensions();
    const maxW = window.innerWidth * 0.85;
    const maxH = window.innerHeight * 0.65;

    // Only scale down, never up
    const scale = Math.min(1, maxW / w, maxH / h);
    const contentWidth = Math.round(w * scale);
    const contentHeight = Math.round(h * scale);

    // Minimum dialog width so small creatives are centered in a reasonable container
    const minDialogWidth = 450;
    const dialogWidth = Math.max(contentWidth, minDialogWidth);

    return { contentWidth, contentHeight, dialogWidth };
  };

  const { contentWidth, contentHeight, dialogWidth } = getDisplayDimensions();

  // Build subtitle with dimensions
  const size = selectedCreativePreview?.size;
  const dimensionLabel = size?.width && size?.height && (size.width > 0 || size.height > 0)
    ? `${size.width} x ${size.height}`
    : null;

  const formattedTimestamp = timestamp
    ? new Date(timestamp).toLocaleString()
    : null;

  const canDownload = previewType === "image" || previewType === "video" || previewType === "audio";

  return (
    <div>
      <Dialog
        open={previewDialogOpen}
        onClose={handleCloseDialog}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth={false}
      >
        <DialogTitle id="alert-dialog-title" disableTypography style={{ paddingBottom: 4 }}>
          <Box display="flex" justifyContent="space-between" alignItems="flex-start">
            <Box>
              <Typography variant="h6">Creative Preview</Typography>
              {creativeName && (
                <Typography
                  variant="body2"
                  color="textSecondary"
                  style={{ maxWidth: dialogWidth - 60, wordBreak: "break-all", marginTop: 2 }}
                >
                  {creativeName}
                </Typography>
              )}
            </Box>
            {canDownload && (
              <Tooltip title="Download creative">
                <IconButton onClick={handleDownload} size="small" style={{ marginLeft: 8 }}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Box display="flex" flexWrap="wrap" style={{ gap: 6, marginTop: 8 }}>
            {creativeId && (
              <Chip label={`ID: ${creativeId}`} size="small" variant="outlined" />
            )}
            {dimensionLabel && (
              <Chip label={dimensionLabel} size="small" variant="outlined" />
            )}
            {creativeType && (
              <Chip label={creativeType} size="small" variant="outlined" />
            )}
            {email && (
              <Chip label={email} size="small" variant="outlined" />
            )}
            {formattedTimestamp && (
              <Chip label={formattedTimestamp} size="small" variant="outlined" />
            )}
          </Box>
        </DialogTitle>
        <DialogContent
          style={{
            overflow: "hidden",
            padding: 8,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minWidth: dialogWidth
          }}
        >
          {/* <AssignedCreativesList placementId={placementId} /> */}
          {previewType === "audio" ? (
            <Box
              display="flex"
              flexDirection="column"
              alignItems="center"
              justifyContent="center"
              style={{ padding: "32px 24px", minWidth: 400 }}
            >
              <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
                Audio Preview
              </Typography>
              <audio
                controls
                src={creativePreviewURL}
                style={{ width: "100%", maxWidth: 500 }}
              />
            </Box>
          ) : previewType === "video" ? (
            <video
              controls
              src={creativePreviewURL}
              style={{ display: "block", width: contentWidth, height: contentHeight }}
            />
          ) : previewType === "image" ? (
            <img
              src={creativePreviewURL}
              alt={creativeName ? `${creativeName} - Preview` : "Creative Preview"}
              style={{ display: "block", width: contentWidth, height: contentHeight, objectFit: "contain" }}
            />
          ) : (
            <iframe
              src={creativePreviewURL}
              title="Creative Preview"
              width={contentWidth}
              height={contentHeight}
              style={{ display: "block", border: "none" }}
              scrolling="no"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary" disableElevation>
            Back
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
